import { FC, useState, useEffect } from "react"
import { useAuth } from "../../auth"
import { useProcurementPermissions } from '../core/permissionAccess'
import { PROCUREMENT_PERMS } from '../core/permissions'
import { libraryUiScope } from '../core/procurementLibraryUiScope'
import { procurementTenantCgaId } from '../core/procurementTenantContext'
import { toast } from 'react-toastify'
import { CompanyGroupAdmin, SubCompanyAdmin, AccountingAccount, SubAccount, Vessel } from "../core/_models"
import { getCompanyGroupAdmins, getSubCompanyAdmins, getAccountingAccountsWithParams, getSubAccounts, createSubCatalogue, getVesselsByCompany } from "../core/_requests"
import SearchableSelect from '../../../shared/components/SearchableSelect'

interface SubCatalogueModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: () => void
}

const SubCatalogueModal: FC<SubCatalogueModalProps> = ({ visible, onClose, onSubmit }) => {
  const { currentUser } = useAuth()
  const { getScope } = useProcurementPermissions()
  const libraryScope = libraryUiScope(
    getScope,
    PROCUREMENT_PERMS.LIBRARY_SUBCATEGORY_MANAGE,
    PROCUREMENT_PERMS.LIBRARY_SUBCATEGORY_VIEW,
  )
  const libraryScopeAll = libraryScope === 'ALL'
  const libraryScopeCompany = libraryScope === 'COMPANY'

  const tenantCgaId = procurementTenantCgaId(currentUser)
  const effectiveCgaId = libraryScopeCompany ? tenantCgaId : undefined

  const [formData, setFormData] = useState({
    company: '',
    subCompany: '',
    accountingCode: '',
    subaccountId: '',
    name: '',
    vesselId: ''
  })
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // State for all dropdowns
  const [companies, setCompanies] = useState<CompanyGroupAdmin[]>([])
  const [subCompanies, setSubCompanies] = useState<SubCompanyAdmin[]>([])
  const [accountingAccounts, setAccountingAccounts] = useState<AccountingAccount[]>([])
  const [subAccounts, setSubAccounts] = useState<SubAccount[]>([])
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(false)
  const [isLoadingSubAccounts, setIsLoadingSubAccounts] = useState(false)
  
  // State for vessels
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [isLoadingVessels, setIsLoadingVessels] = useState(false)

  // Load companies when modal opens (only for superadmin)
  useEffect(() => {
    if (visible && libraryScopeAll) {
      loadCompanies()
    }
  }, [visible, libraryScopeAll])

  // Load vessels when modal opens for COMPANY-scoped library users (tenant CGA resolved server-side).
  useEffect(() => {
    if (visible && libraryScopeCompany && effectiveCgaId) {
      loadVesselsForCompany()
    }
  }, [visible, libraryScopeCompany, effectiveCgaId])

  // Load subcompanies and vessels when company is selected (for superadmin)
  useEffect(() => {
    if (formData.company) {
      loadSubCompanies(parseInt(formData.company))
      loadVesselsByCompany()
    } else {
      setSubCompanies([])
      setVessels([])
      // Reset vessel when company changes
      setFormData(prev => ({ ...prev, vesselId: '' }))
    }
  }, [formData.company])

  // Load accounting accounts when company and vessel are selected
  useEffect(() => {
    if (formData.company && formData.vesselId) {
      loadAccountingAccounts(parseInt(formData.company), Number(formData.vesselId))
    } else if (formData.vesselId && !libraryScopeAll) {
      const cgaIdToUse = effectiveCgaId
      if (cgaIdToUse) {
        loadAccountingAccounts(cgaIdToUse, Number(formData.vesselId))
      } else {
        setAccountingAccounts([])
      }
    } else {
      setAccountingAccounts([])
    }
  }, [formData.company, formData.vesselId, effectiveCgaId])

  // Load sub accounts when accounting code is selected
  useEffect(() => {
    // For superadmin, require company, accounting code, and vessel
    if (libraryScopeAll) {
      if (formData.accountingCode && formData.company && formData.vesselId) {
        loadSubAccounts(parseInt(formData.accountingCode))
      } else {
        setSubAccounts([])
      }
    } else {
      // For non-superadmin, only require accounting code and vessel
      if (formData.accountingCode && formData.vesselId) {
        loadSubAccounts(parseInt(formData.accountingCode))
      } else {
        setSubAccounts([])
      }
    }
  }, [formData.accountingCode, formData.company, formData.vesselId, libraryScopeAll])

  const loadCompanies = async () => {
    setIsLoadingDropdowns(true)
    try {
      const companiesData = await getCompanyGroupAdmins()
      setCompanies(companiesData)
    } catch (error) {
      console.error('Error loading companies:', error)
    } finally {
      setIsLoadingDropdowns(false)
    }
  }

  const loadSubCompanies = async (companyId: number) => {
    try {
      const subCompaniesData = await getSubCompanyAdmins(companyId)
      setSubCompanies(subCompaniesData)
    } catch (error) {
      console.error('Error loading sub companies:', error)
      setSubCompanies([])
    }
  }

  const loadVesselsForCompany = async () => {
    if (!effectiveCgaId) return

    setIsLoadingVessels(true)
    try {
      // Get company username from current user (no need to call getCompanyGroupAdmins)
      const companyUsername = (currentUser as any)?.companyGroupAdmin?.username || (currentUser as any)?.username
      
      if (!companyUsername) {
        console.error('Company username not found in current user')
        return
      }

      console.log('Loading vessels for company username:', companyUsername)
      const vesselsData = await getVesselsByCompany(companyUsername)
      setVessels(vesselsData)
      console.log('Loaded vessels:', vesselsData.length)
    } catch (error) {
      console.error('Error loading vessels for company:', error)
      setVessels([])
    } finally {
      setIsLoadingVessels(false)
    }
  }

  const loadVesselsByCompany = async () => {
    if (!formData.company) return
    
    setIsLoadingVessels(true)
    try {
      // Find the selected company to get its username
      const selectedCompany = companies.find(c => c.id === parseInt(formData.company))
      if (!selectedCompany) {
        console.error('Selected company not found')
        return
      }

      const vesselsData = await getVesselsByCompany(selectedCompany.username)
      setVessels(vesselsData)
    } catch (error) {
      console.error('Error loading vessels by company:', error)
      setVessels([])
    } finally {
      setIsLoadingVessels(false)
    }
  }

  const loadAccountingAccounts = async (cgaId: number, vesselId: number) => {
    setIsLoadingDropdowns(true)
    try {
      const accountsData = await getAccountingAccountsWithParams(cgaId, vesselId)
      setAccountingAccounts(accountsData)
    } catch (error) {
      console.error('Error loading accounting accounts:', error)
      setAccountingAccounts([])
    } finally {
      setIsLoadingDropdowns(false)
    }
  }

  const loadSubAccounts = async (accountId: number) => {
    setIsLoadingSubAccounts(true)
    try {
      // Determine which company ID to use
      let cgaIdToUse: number | undefined

      if (libraryScopeAll) {
        // For superadmin, use selected company
        cgaIdToUse = formData.company ? parseInt(formData.company) : undefined
      } else {
        // For non-superadmin, use their company ID
        cgaIdToUse = effectiveCgaId ?? tenantCgaId
      }

      const vesselId = formData.vesselId ? Number(formData.vesselId) : undefined
      const subAccountData = await getSubAccounts(accountId, cgaIdToUse, vesselId)
      setSubAccounts(subAccountData) // API returns array
    } catch (error) {
      console.error('Error loading sub accounts:', error)
      setSubAccounts([])
    } finally {
      setIsLoadingSubAccounts(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    // Company validation for superadmin
    if (libraryScopeAll && !formData.company) {
      newErrors.company = 'Company is required'
    }

    if (!formData.accountingCode) newErrors.accountingCode = 'Accounting Code is required'
    if (!formData.subaccountId) newErrors.subaccountId = 'Sub Accounting Code is required'
    if (!formData.name.trim()) newErrors.name = 'Sub Catalogue Name is required'
    if (!formData.vesselId) newErrors.vesselId = 'Vessel is required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return
    setIsSubmitting(true)
    try {
      // Determine the correct CGA ID to use
      let cgaIdToUse: number

      if (libraryScopeAll) {
        // For superadmin, use selected company
        if (formData.company) {
          cgaIdToUse = parseInt(formData.company)
        } else {
          toast.error('Please select a company')
          setIsSubmitting(false)
          return
        }
      } else {
        const resolved = effectiveCgaId ?? tenantCgaId
        if (!resolved) {
          toast.error('Unable to resolve company scope for this user')
          setIsSubmitting(false)
          return
        }
        cgaIdToUse = resolved
      }

      const selectedVessel = vessels.find(v => v.id === Number(formData.vesselId))
      const result = await createSubCatalogue({
        name: formData.name,
        vesselId: Number(formData.vesselId),
        vesselType: selectedVessel?.vesselType,
        cgaid: { id: cgaIdToUse },
        subaccounts: [{ id: parseInt(formData.subaccountId) }]
      })
      console.log('SubCatalogueModal - Created subcatalogue:', result)
      toast.success('Sub Catalogue added successfully!')
      await onSubmit()
      handleClose()
    } catch (error: any) {
      console.error('Error adding sub catalogue:', error)
      toast.error('Failed to add sub catalogue')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setFormData({
      company: '',
      subCompany: '',
      accountingCode: '',
      subaccountId: '',
      name: '',
      vesselId: ''
    })
    setErrors({})
    setCompanies([])
    setSubCompanies([])
    setAccountingAccounts([])
    setSubAccounts([])
    setVessels([])
    onClose()
  }

  if (!visible) return null

  return (
    <div className="modal fade show d-flex align-items-center justify-content-center" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 1050 }}>
      <div className='modal-dialog modal-lg modal-dialog-centered' role='document'>
        <div className='modal-content bg-white' style={{ color: '#181C32' }}>
          <div className='modal-header'>
            <h5 className='modal-title'>Add Sub Catalogue</h5>
            <button type='button' className='btn-close' onClick={handleClose}></button>
          </div>
          <div className='modal-body'>
            <div className='row g-3'>

              {/* Company dropdown - only for superadmin */}
              {libraryScopeAll && (
                <div className='col-md-6'>
                  <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Company</label>
                  <SearchableSelect
                    className={`form-select ${errors.company ? 'is-invalid' : ''}`}
                    name='company'
                    value={formData.company}
                    onChange={handleInputChange}
                    disabled={isLoadingDropdowns}
                  >
                    <option value=''>Select Company</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </SearchableSelect>
                  {errors.company && <div className='invalid-feedback'>{errors.company}</div>}
                </div>
              )}

              {/* Subcompany dropdown - only for superadmin when company is selected */}
              {libraryScopeAll && formData.company && (
                <div className='col-md-6'>
                  <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Sub Company</label>
                  <SearchableSelect
                    className='form-select'
                    name='subCompany'
                    value={formData.subCompany}
                    onChange={handleInputChange}
                  >
                    <option value=''>Select Sub Company (Optional)</option>
                    {subCompanies.map(subCompany => (
                      <option key={subCompany.id} value={subCompany.id}>{subCompany.name}</option>
                    ))}
                  </SearchableSelect>
                </div>
              )}

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Vessel</label>
                <SearchableSelect
                  className={`form-select ${errors.vesselId ? 'is-invalid' : ''}`}
                  name='vesselId'
                  value={formData.vesselId}
                  onChange={handleInputChange}
                  disabled={libraryScopeAll && (!formData.company || isLoadingVessels)}
                >
                  <option value=''>
                    {isLoadingVessels ? 'Loading vessels...' :
                      (libraryScopeAll && !formData.company) ? 'Please select a company first' :
                        'Select Vessel'}
                  </option>
                  {vessels.map(vessel => (
                    <option key={vessel.id} value={vessel.id}>
                      {vessel.fleet_name}
                    </option>
                  ))}
                </SearchableSelect>
                {errors.vesselId && <div className='invalid-feedback'>{errors.vesselId}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Accounting Code</label>
                <SearchableSelect
                  className={`form-select ${errors.accountingCode ? 'is-invalid' : ''}`}
                  name='accountingCode'
                  value={formData.accountingCode}
                  onChange={handleInputChange}
                  disabled={isLoadingDropdowns || (libraryScopeAll && (!formData.company || !formData.vesselId))}
                >
                  <option value=''>
                    {isLoadingDropdowns ? 'Loading accounting codes...' :
                      (libraryScopeAll && !formData.company) ? 'Please select a company first' :
                        !formData.vesselId ? 'Please select a vessel first' :
                          'Select Accounting Code'}
                  </option>
                  {accountingAccounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.accountCodes}
                    </option>
                  ))}
                </SearchableSelect>
                {errors.accountingCode && <div className='invalid-feedback'>{errors.accountingCode}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Sub Accounting Code</label>
                <SearchableSelect
                  className={`form-select ${errors.subaccountId ? 'is-invalid' : ''}`}
                  name='subaccountId'
                  value={formData.subaccountId}
                  onChange={handleInputChange}
                  disabled={isLoadingSubAccounts || !formData.accountingCode || !formData.vesselId || (libraryScopeAll && !formData.company)}
                >
                  <option value=''>
                    {isLoadingSubAccounts ? 'Loading sub accounting codes...' :
                      (libraryScopeAll && !formData.company) ? 'Please select a company first' :
                        !formData.vesselId ? 'Please select a vessel first' :
                          !formData.accountingCode ? 'Please select an accounting code first' :
                            'Select Sub Accounting Code'}
                  </option>
                  {subAccounts.map(subAccount => (
                    <option key={subAccount.id} value={subAccount.id}>
                      {subAccount.subaccountCodes}
                    </option>
                  ))}
                </SearchableSelect>
                {errors.subaccountId && <div className='invalid-feedback'>{errors.subaccountId}</div>}
              </div>

              <div className='col-12'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Sub Catalogue Name</label>
                <input
                  type='text'
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  name='name'
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder='e.g., Electrical Spares Master'
                  style={{ color: '#000' }}
                />
                {errors.name && <div className='invalid-feedback'>{errors.name}</div>}
              </div>
            </div>
          </div>
          <div className='modal-footer'>
            <button type='button' className='btn btn-light btn-sm' onClick={handleClose} disabled={isSubmitting}>Cancel</button>
            <button type='button' className='btn btn_primary' onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Adding...' : 'Add Sub Catalogue'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SubCatalogueModal;
